import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, Badge, Flex, Spinner, Alert, AlertIcon,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Table, Thead, Tbody, Tr, Th, Td,
  Stat, StatLabel, StatNumber, StatGroup,
  Input, Select, Button, HStack, SimpleGrid, Code, Icon,
  Progress, Textarea, Tooltip, useToast,
} from '@chakra-ui/react';
import { FiRefreshCw, FiTerminal, FiDownload } from 'react-icons/fi';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('telemetry_token')}`,
});

const PostgresStatus = () => {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [tables, setTables] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [tablePage, setTablePage] = useState(1);
  const [tableSort, setTableSort] = useState('size');
  const [tableSearch, setTableSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [querySql, setQuerySql] = useState(
    "SELECT table_name, pg_size_pretty(pg_total_relation_size(quote_ident(table_name))) AS size\n" +
    "FROM information_schema.tables\n" +
    "WHERE table_schema = 'public'\n" +
    "ORDER BY pg_total_relation_size(quote_ident(table_name)) DESC\n" +
    "LIMIT 20;"
  );
  const [queryResult, setQueryResult] = useState(null);
  const [queryRunning, setQueryRunning] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/postgres/status/', { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) setStatus(data.postgres);
      else setError(data.message || 'Failed to fetch status');
    } catch (e) { setError(e.message); }
  }, []);

  const fetchTables = useCallback(async () => {
    try {
      const res = await fetch(
        `/api/v1/postgres/tables/?page=${tablePage}&sort=${tableSort}&search=${encodeURIComponent(tableSearch)}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (data.success) setTables(data);
      else setError(data.message);
    } catch (e) { setError(e.message); }
  }, [tablePage, tableSort, tableSearch]);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/postgres/activity/', { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) setActivity(data.activity);
      else setError(data.message);
    } catch (e) { setError(e.message); }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([
      fetchStatus(),
      tabIndex === 1 ? fetchTables() : Promise.resolve(),
      tabIndex === 2 ? fetchActivity() : Promise.resolve(),
    ]);
    setLoading(false);
  }, [fetchStatus, fetchTables, fetchActivity, tabIndex]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(refreshAll, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshAll]);

  useEffect(() => {
    if (tabIndex === 1) fetchTables();
    if (tabIndex === 2) fetchActivity();
  }, [tabIndex, fetchTables, fetchActivity]);

  const formatUptime = (startTime) => {
    if (!startTime) return 'N/A';
    const diff = Math.floor((Date.now() - new Date(startTime)) / 1000);
    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const executeQuery = async () => {
    if (!querySql.trim()) return;
    setQueryRunning(true);
    setQueryResult(null);
    try {
      const res = await fetch('/api/v1/postgres/query/', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ sql: querySql }),
      });
      const data = await res.json();
      setQueryResult(data);
      if (!data.success) {
        toast({ title: data.message || 'Query failed', status: 'error', duration: 5000 });
      }
    } catch {
      toast({ title: 'Error executing query', status: 'error', duration: 3000 });
    }
    setQueryRunning(false);
  };

  const downloadCsv = () => {
    if (!queryResult?.rows?.length) return;
    const cols = queryResult.columns;
    const escape = (val) => {
      const s = val === null || val === undefined ? '' : String(val);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [cols.map(escape).join(','), ...queryResult.rows.map(r => cols.map(c => escape(r[c])).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `query_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="gray.800">PostgreSQL Status</Heading>
          <Text color="gray.500">Monitor database health, tables, and active queries</Text>
        </Box>
        <HStack>
          <Button size="sm" variant={autoRefresh ? 'solid' : 'outline'} colorScheme="purple"
            onClick={() => setAutoRefresh(v => !v)}>
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button size="sm" leftIcon={<Icon as={FiRefreshCw} />} onClick={refreshAll} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </Flex>

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />{error}
        </Alert>
      )}
      {status?.error && (
        <Alert status="warning" mb={4} borderRadius="md">
          <AlertIcon />{status.error}
        </Alert>
      )}

      <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="purple">
        <TabList>
          <Tab>Overview</Tab>
          <Tab>Tables</Tab>
          <Tab>Active Queries</Tab>
          <Tab><HStack spacing={2}><Icon as={FiTerminal} boxSize={3.5} /><Text>Query</Text></HStack></Tab>
        </TabList>

        <TabPanels>
          {/* Overview */}
          <TabPanel px={0}>
            {loading && !status ? (
              <Flex justify="center" py={20}><Spinner size="xl" color="purple.500" /></Flex>
            ) : status?.connected ? (
              <>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
                  {[
                    { label: 'Server Version', value: status.server_info?.version },
                    { label: 'Uptime', value: formatUptime(status.server_info?.start_time) },
                    { label: 'Database Size', value: status.database?.size_pretty },
                    { label: 'Latency', value: `${status.server_info?.latency_ms} ms` },
                  ].map(({ label, value }) => (
                    <Box key={label} bg="white" p={5} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                      <StatGroup><Stat>
                        <StatLabel color="gray.500">{label}</StatLabel>
                        <StatNumber fontSize="lg" color="gray.800">{value}</StatNumber>
                      </Stat></StatGroup>
                    </Box>
                  ))}
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
                  <Box bg="white" p={5} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                    <Heading size="sm" mb={3} color="gray.700">Connections</Heading>
                    <Text fontSize="sm" mb={1}>Active: <Badge colorScheme="green">{status.connections?.active}</Badge></Text>
                    <Text fontSize="sm" mb={1}>Idle: <Badge colorScheme="yellow">{status.connections?.idle}</Badge></Text>
                    <Text fontSize="sm" mb={2}>Total: <Badge colorScheme="blue">{status.connections?.total}</Badge> / {status.connections?.max}</Text>
                    <Progress
                      value={(status.connections?.total / status.connections?.max) * 100}
                      colorScheme="purple" borderRadius="full" size="sm"
                    />
                  </Box>
                  <Box bg="white" p={5} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                    <Heading size="sm" mb={3} color="gray.700">Configuration</Heading>
                    {status.config && Object.entries(status.config).map(([k, v]) => (
                      <Flex key={k} justify="space-between" py={1} borderBottom="1px solid" borderColor="gray.100">
                        <Code fontSize="sm" bg="purple.50" color="purple.700">{k}</Code>
                        <Text fontSize="sm" color="gray.600">{v}</Text>
                      </Flex>
                    ))}
                  </Box>
                </SimpleGrid>

                <Box bg="white" p={5} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                  <Heading size="sm" mb={3} color="gray.700">Database Info</Heading>
                  <Text fontSize="sm" color="gray.600">Name: <strong>{status.database?.name}</strong></Text>
                  <Text fontSize="sm" color="gray.600">Tables: <strong>{status.tables?.count}</strong></Text>
                </Box>
              </>
            ) : !loading && (
              <Text color="gray.500">PostgreSQL is not connected.</Text>
            )}
          </TabPanel>

          {/* Tables */}
          <TabPanel px={0}>
            <HStack mb={4}>
              <Input
                placeholder="Search tables..."
                value={tableSearch}
                onChange={(e) => { setTableSearch(e.target.value); setTablePage(1); }}
                maxW="300px"
                bg="white"
                focusBorderColor="purple.500"
              />
              <Select
                value={tableSort}
                onChange={(e) => setTableSort(e.target.value)}
                maxW="200px"
                bg="white"
                focusBorderColor="purple.500"
              >
                <option value="size">Sort by Size</option>
                <option value="name">Sort by Name</option>
                <option value="rows">Sort by Rows</option>
              </Select>
            </HStack>
            {tables ? (
              <>
                <Box bg="white" borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100" overflowX="auto">
                  <Table size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Table</Th><Th>Size</Th><Th isNumeric>Rows (est)</Th>
                        <Th>Last Vacuum</Th><Th>Last Analyze</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {tables.tables?.map((t) => (
                        <Tr key={t.tablename} _hover={{ bg: 'gray.50' }}>
                          <Td><Code fontSize="sm" bg="purple.50" color="purple.700">{t.tablename}</Code></Td>
                          <Td fontSize="sm">{t.size_pretty}</Td>
                          <Td isNumeric fontSize="sm">{Number(t.row_estimate).toLocaleString()}</Td>
                          <Td fontSize="xs" color="gray.600">
                            {t.last_vacuum === 'Never'
                              ? <Badge colorScheme="red" fontSize="xs">Never</Badge>
                              : new Date(t.last_vacuum).toLocaleDateString()}
                          </Td>
                          <Td fontSize="xs" color="gray.600">
                            {t.last_analyze === 'Never'
                              ? <Badge colorScheme="red" fontSize="xs">Never</Badge>
                              : new Date(t.last_analyze).toLocaleDateString()}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
                {tables.pagination && (
                  <HStack mt={4} justify="center">
                    <Button size="sm" onClick={() => setTablePage(p => Math.max(1, p - 1))} isDisabled={tablePage <= 1}>Prev</Button>
                    <Text fontSize="sm" color="gray.500">Page {tables.pagination.page} of {tables.pagination.pages}</Text>
                    <Button size="sm" onClick={() => setTablePage(p => p + 1)} isDisabled={tablePage >= tables.pagination.pages}>Next</Button>
                  </HStack>
                )}
              </>
            ) : <Flex justify="center" py={10}><Spinner color="purple.500" /></Flex>}
          </TabPanel>

          {/* Activity */}
          <TabPanel px={0}>
            {activity ? (
              <Box bg="white" borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100" overflowX="auto">
                <Table size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>PID</Th><Th>User</Th><Th>State</Th><Th>Duration</Th><Th>Query</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {activity.map((a) => (
                      <Tr key={a.pid} _hover={{ bg: 'gray.50' }}>
                        <Td fontSize="sm">{a.pid}</Td>
                        <Td fontSize="sm">{a.usename}</Td>
                        <Td>
                          <Badge colorScheme={a.state === 'active' ? 'green' : a.state === 'idle' ? 'yellow' : 'gray'}>
                            {a.state}
                          </Badge>
                        </Td>
                        <Td fontSize="sm">{a.query_duration_secs != null ? `${a.query_duration_secs}s` : '—'}</Td>
                        <Td maxW="400px" isTruncated><Code fontSize="xs">{a.query}</Code></Td>
                      </Tr>
                    ))}
                    {activity.length === 0 && (
                      <Tr><Td colSpan={5} textAlign="center" color="gray.400" py={6}>No active connections</Td></Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            ) : <Flex justify="center" py={10}><Spinner color="purple.500" /></Flex>}
          </TabPanel>

          {/* Query */}
          <TabPanel px={0}>
            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100" mb={4}>
              <HStack spacing={2} mb={4}>
                <Icon as={FiTerminal} color="gray.600" />
                <Heading size="md" color="gray.800">SQL Query</Heading>
                <Badge colorScheme="yellow" fontSize="xs">Read-only</Badge>
              </HStack>
              <Textarea
                value={querySql}
                onChange={(e) => setQuerySql(e.target.value)}
                placeholder="SELECT * FROM ..."
                fontFamily="mono"
                fontSize="sm"
                minH="150px"
                bg="gray.900"
                color="green.300"
                border="none"
                borderRadius="md"
                _placeholder={{ color: 'gray.500' }}
                _focus={{ border: 'none', boxShadow: 'none' }}
                mb={3}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    executeQuery();
                  }
                }}
              />
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.400">⌘+Enter to execute. Write operations are blocked.</Text>
                <HStack spacing={2}>
                  {queryResult?.rows?.length > 0 && (
                    <Button leftIcon={<Icon as={FiDownload} />} size="sm" colorScheme="green" variant="outline" onClick={downloadCsv}>
                      Download CSV
                    </Button>
                  )}
                  <Button colorScheme="purple" size="sm" onClick={executeQuery} isLoading={queryRunning} loadingText="Running...">
                    Execute
                  </Button>
                </HStack>
              </HStack>
            </Box>

            {queryResult && (
              <Box bg="white" borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100" overflow="hidden">
                <HStack
                  p={4}
                  bg={queryResult.success ? 'green.50' : 'red.50'}
                  borderBottom="1px" borderColor="gray.200"
                  justify="space-between"
                >
                  <HStack spacing={3}>
                    <Badge colorScheme={queryResult.success ? 'green' : 'red'}>
                      {queryResult.success ? 'Success' : 'Error'}
                    </Badge>
                    {queryResult.success && (
                      <>
                        <Text fontSize="sm" color="gray.600">{queryResult.row_count} row{queryResult.row_count !== 1 ? 's' : ''}</Text>
                        <Text fontSize="sm" color="gray.400">•</Text>
                        <Text fontSize="sm" color="gray.600">{queryResult.duration_ms} ms</Text>
                      </>
                    )}
                  </HStack>
                  {queryResult?.rows?.length > 0 && (
                    <Button leftIcon={<Icon as={FiDownload} />} size="xs" colorScheme="green" variant="ghost" onClick={downloadCsv}>CSV</Button>
                  )}
                </HStack>

                {!queryResult.success ? (
                  <Box p={4}>
                    <Code colorScheme="red" fontSize="sm" whiteSpace="pre-wrap">{queryResult.message}</Code>
                  </Box>
                ) : queryResult.columns?.length > 0 ? (
                  <Box overflowX="auto" maxH="500px" overflowY="auto">
                    <Table size="sm">
                      <Thead position="sticky" top={0} bg="white" zIndex={1}>
                        <Tr>{queryResult.columns.map((col, i) => <Th key={i} fontSize="xs">{col}</Th>)}</Tr>
                      </Thead>
                      <Tbody>
                        {queryResult.rows.map((row, ri) => (
                          <Tr key={ri} _hover={{ bg: 'gray.50' }}>
                            {queryResult.columns.map((col, ci) => (
                              <Td key={ci} fontSize="xs" maxW="300px">
                                <Tooltip label={String(row[col] ?? '')} placement="top-start">
                                  <Text isTruncated>
                                    {row[col] === null
                                      ? <Text as="span" color="gray.400" fontStyle="italic">NULL</Text>
                                      : String(row[col])}
                                  </Text>
                                </Tooltip>
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ) : (
                  <Box p={4} textAlign="center">
                    <Text color="gray.500" fontSize="sm">Query executed successfully (no rows returned)</Text>
                  </Box>
                )}
              </Box>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default PostgresStatus;
